import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ContactForm from "./components/ContactForm";

const positions = [
  { icon: "ri-route-line", title: "以检索为核心", text: "把「选番」这件事做到极致，让每个人都能用年份、评分、标签快速锁定想看的动漫。" },
  { icon: "ri-database-2-line", title: "做精不贪多", text: "只收录经过口碑验证的好番，认真整理每一部作品的信息，让内容真正有价值。" },
  { icon: "ri-community-line", title: "与番友同行", text: "从观众的真实需求出发持续迭代，做一个真正懂追番人的动漫检索库。" },
];

const principles = [
  { icon: "ri-focus-3-line", title: "真实准确", text: "信息收录以作品客观资料为准，不夸大、不虚构，方便观众做出判断。" },
  { icon: "ri-exchange-funds-line", title: "公平推荐", text: "榜单与推荐综合多维度口碑，不偏向任何单一平台或作品。" },
  { icon: "ri-book-open-line", title: "持续更新", text: "紧跟新番与剧场版节奏，让每一季的新内容都能被及时找到。" },
  { icon: "ri-heart-line", title: "尊重创作", text: "尊重每一位创作者与观众，让好作品被更多人看见。" },
];

const testimonials = [
  { name: "追番十年的小圆", text: "按评分排序补番太省心了，一口气把高口碑剧场版都补完了。" },
  { name: "上班族的阿哲", text: "标签筛选很准，想找治愈番的时候一秒定位，再也不踩雷。" },
  { name: "动漫区UP主小莫", text: "榜单质量很靠谱，做盘点视频时经常来这里查资料。" },
];

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于牛奶动漫库",
  url: "https://comicsrq.github.io/guanyu",
  description:
    "牛奶动漫库是以番剧检索为核心的动漫库，提供按年份、评分、标签筛选番剧的高级检索，支持OVA与剧场版分区，以及高口碑榜单与隐藏目录入口。",
  inLanguage: "zh-CN",
};

export default function Guanyu() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <Navbar />
      <main className="bg-background-50 pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <a href="/" className="hover:text-primary-600">首页</a>
              <span className="mx-1.5">/</span>
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-900 md:text-3xl">关于牛奶动漫库</h1>
            <p className="mt-2 text-sm text-foreground-600">认识一下这个陪你找番的动漫检索库。</p>
          </div>
        </div>

        <section className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 lg:grid-cols-12 md:px-8">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary-600">
              <i className="ri-quill-pen-line"></i> Who We Are
            </span>
            <h2 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-900 md:text-3xl">
              把「今天看什么」，
              <br className="hidden md:block" />
              变成一件轻松的事
            </h2>
            <p className="mt-5 text-sm leading-7 text-foreground-600">
              牛奶动漫库诞生于一个朴素的愿望：让每一个想找番的人，都不用再在茫茫列表中迷失。我们用「按年份检索、按评分排序、按标签筛选」这三种最直接的方式，帮你把海量番剧变得井井有条。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              从年度新番到经典剧场版，从热血神作到冷门遗珠，我们认真整理每一部作品的信息，并沉淀出高口碑榜单与隐藏目录，让真正的好番不被埋没。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "1200+ 部番剧持续收录",
                "剧场版 / OVA 独立分区直达",
                "18 个细分标签叠加检索",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-primary-600"></i> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://readdy.ai/api/search-image?query=Artistic%20anime%20illustration%20of%20a%20cozy%20room%20filled%20with%20books%2C%20movie%20reels%20and%20floating%20sakura%2C%20a%20cat%20watching%20a%20screen%20glowing%20warmly%2C%20cream%20and%20amber%20palette%2C%20welcoming%20editorial%20mood%2C%20high%20detail%20painting&width=800&height=900&seq=milk-about&orientation=portrait"
              alt="牛奶动漫库 关于我们插图"
              title="牛奶动漫库 品牌介绍"
              className="h-full w-full rounded-lg object-cover"
            />
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-accent-600">
                <i className="ri-compass-3-line"></i> Our Position
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">我们的定位</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {positions.map((p) => (
                <div key={p.title} className="rounded-lg bg-background-50 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-secondary-700">
                <i className="ri-flag-2-line"></i> Our Principles
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">内容原则</h2>
              <p className="mt-3 text-sm leading-6 text-foreground-600">
                做内容，先对得起每一位点进来的观众。我们用四条原则约束每一页信息的收录与呈现。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-8">
              {principles.map((p) => (
                <div key={p.title} className="flex gap-4 rounded-lg bg-background-100 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-900">{p.title}</h3>
                    <p className="mt-1.5 text-sm leading-6 text-foreground-600">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary-600">
                <i className="ri-chat-quote-line"></i> Reviews
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">番友的声音</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-background-50 p-6">
                  <div className="flex items-center gap-0.5 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill"></i>
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-7 text-foreground-700">「{t.text}」</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2 text-sm font-medium text-foreground-800">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-200 text-secondary-900">
                      {t.name.slice(0, 1)}
                    </span>
                    {t.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 lg:grid-cols-12 md:px-8">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-accent-600">
              <i className="ri-mail-open-line"></i> Contact
            </span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-900 md:text-3xl">联系我们</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">
              任何关于番剧收录、榜单建议或网站反馈，都欢迎与我们聊聊。
            </p>
            <ul className="mt-6 space-y-4">
              <li className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-mail-line"></i>
                </span>
                contact@milk-anime.example
              </li>
              <li className="flex items-center gap-3 text-sm text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-chat-3-line"></i>
                </span>
                站内留言表单（推荐）
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}